/**
 * usePlaceSearch — recherche de lieu a chargement progressif.
 *
 * POURQUOI CE HOOK
 * ----------------
 * La recherche du formulaire d'intervention tenait en un `debounce` maison et
 * un `setState` en fin de promesse. Trois defauts en decoulaient :
 *
 *   - RESULTATS PERIMES. Rien n'annulait une requete devenue inutile : la
 *     reponse lente de « euro » pouvait arriver APRES celle de « euromaster »
 *     et l'ecraser. Ici, chaque nouvelle saisie annule la requete en cours
 *     (AbortController) ET incremente une generation : une reponse d'une
 *     generation passee est ignoree meme si l'annulation arrive trop tard.
 *
 *   - PREMIERE PAGE SEULEMENT. L'API coupait a 8 lignes, et le client n'avait
 *     aucun moyen d'en demander plus. `loadMore()` charge la page suivante,
 *     l'AJOUTE a la liste, et s'arrete quand l'API repond `has_more: false`.
 *
 *   - PANNE MUETTE. Toute erreur devenait une liste vide, affichee comme
 *     « Aucun lieu trouve ». L'etat `error` distingue desormais les deux, et
 *     `retry()` rejoue exactement la requete qui a echoue.
 *
 * Garanties : jamais deux requetes simultanees pour la meme liste, jamais de
 * doublon (dedoublonnage par `place_id`), aucun timer ni requete qui survive
 * au demontage du composant.
 */
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  AddressSearchPage,
  AddressSearchPageOptions,
  AddressSuggestion,
  DEFAULT_PAGE_SIZE,
  MIN_QUERY_LENGTH,
  isAbortError,
  searchAddressesPage,
} from "../services/addressSearch";

export type PlaceSearchStatus =
  /** Rien a chercher (saisie trop courte). */
  | "idle"
  /** Premiere page en cours (debounce compris). */
  | "loading"
  /** Page suivante en cours ; la liste deja chargee reste affichee. */
  | "loadingMore"
  | "success"
  | "error";

export interface PlaceSearchState {
  /** La requete a laquelle `items` repond — pas forcement la saisie en cours. */
  query: string;
  items: AddressSuggestion[];
  attribution: string | null;
  page: number;
  hasMore: boolean;
  total: number | null;
  status: PlaceSearchStatus;
  /** Quelle etape a echoue : la premiere page, ou une page suivante. */
  errorOn: "initial" | "more" | null;
}

export interface UsePlaceSearchOptions {
  debounceMs?: number;
  pageSize?: number;
  coords?: { lat: number; lng: number } | null;
  /** Injectable pour les tests. */
  searchFn?: (query: string, options: AddressSearchPageOptions) => Promise<AddressSearchPage>;
}

const INITIAL: PlaceSearchState = {
  query: "",
  items: [],
  attribution: null,
  page: 0,
  hasMore: false,
  total: null,
  status: "idle",
  errorOn: null,
};

type Action =
  | { type: "reset" }
  | { type: "start"; query: string }
  | { type: "startMore" }
  | { type: "page"; result: AddressSearchPage; append: boolean }
  | { type: "error"; on: "initial" | "more" };

/** Ajoute `incoming` a `current` sans jamais dupliquer un `place_id`. */
export function mergeUnique(current: AddressSuggestion[], incoming: AddressSuggestion[]): AddressSuggestion[] {
  const seen = new Set(current.map((s) => s.place_id));
  const merged = current.slice();
  for (const item of incoming) {
    if (seen.has(item.place_id)) continue;
    seen.add(item.place_id);
    merged.push(item);
  }
  return merged;
}

function reducer(state: PlaceSearchState, action: Action): PlaceSearchState {
  switch (action.type) {
    case "reset":
      return INITIAL;
    case "start":
      // Nouvelle requete : la liste precedente est VIDEE, jamais melangee.
      return { ...INITIAL, query: action.query, status: "loading" };
    case "startMore":
      return { ...state, status: "loadingMore", errorOn: null };
    case "page": {
      const { result, append } = action;
      return {
        ...state,
        items: append ? mergeUnique(state.items, result.suggestions) : mergeUnique([], result.suggestions),
        attribution: append ? state.attribution : result.attribution,
        page: result.page,
        hasMore: result.hasMore,
        // Le total n'est renvoye qu'en page 1 : on le conserve ensuite.
        total: result.total ?? state.total,
        status: "success",
        errorOn: null,
      };
    }
    case "error":
      return { ...state, status: "error", errorOn: action.on };
    default:
      return state;
  }
}

export function usePlaceSearch({
  debounceMs = 300,
  pageSize = DEFAULT_PAGE_SIZE,
  coords = null,
  searchFn = searchAddressesPage,
}: UsePlaceSearchOptions = {}) {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  // Les refs portent ce que les callbacks doivent lire A JOUR, sans se
  // recreer — un `loadMore` recree a chaque rendu relancerait l'ecouteur de
  // defilement et perdrait la garde contre les doubles appels.
  const stateRef = useRef(state);
  stateRef.current = state;
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsRef = useRef({ pageSize, coords, searchFn });
  optionsRef.current = { pageSize, coords, searchFn };

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    inFlightRef.current = false;
  }, []);

  const fetchPage = useCallback(async (query: string, page: number) => {
    const generation = generationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;

    const { pageSize: perPage, coords: position, searchFn: search } = optionsRef.current;

    try {
      const result = await search(query, { page, perPage, coords: position, signal: controller.signal });
      if (generation !== generationRef.current) return; // reponse perimee
      dispatch({ type: "page", result, append: page > 1 });
    } catch (error) {
      if (generation !== generationRef.current || isAbortError(error)) return;
      dispatch({ type: "error", on: page > 1 ? "more" : "initial" });
    } finally {
      if (generation === generationRef.current) {
        inFlightRef.current = false;
        abortRef.current = null;
      }
    }
  }, []);

  /** A appeler a chaque frappe. Debounce, annule l'ancienne requete. */
  const setQuery = useCallback(
    (raw: string) => {
      const query = (raw ?? "").trim();
      generationRef.current += 1;
      cancelPending();

      if (query.length < MIN_QUERY_LENGTH) {
        dispatch({ type: "reset" });
        return;
      }

      // L'etat passe a « loading » DES la frappe : sans cela, le menu
      // afficherait « Aucun lieu trouve » pendant les 300 ms du debounce.
      dispatch({ type: "start", query });
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fetchPage(query, 1);
      }, debounceMs);
    },
    [cancelPending, fetchPage, debounceMs]
  );

  /** Page suivante. Sans effet si une requete est deja en cours ou s'il n'y a plus rien. */
  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (inFlightRef.current || timerRef.current) return;
    if (!current.hasMore || current.status !== "success") return;

    dispatch({ type: "startMore" });
    fetchPage(current.query, current.page + 1);
  }, [fetchPage]);

  /** Rejoue l'etape qui a echoue. */
  const retry = useCallback(() => {
    const current = stateRef.current;
    if (current.status !== "error" || inFlightRef.current) return;

    if (current.errorOn === "more") {
      dispatch({ type: "startMore" });
      fetchPage(current.query, current.page + 1);
    } else {
      generationRef.current += 1;
      dispatch({ type: "start", query: current.query });
      fetchPage(current.query, 1);
    }
  }, [fetchPage]);

  const reset = useCallback(() => {
    generationRef.current += 1;
    cancelPending();
    dispatch({ type: "reset" });
  }, [cancelPending]);

  // Demontage : aucun timer ni requete ne doit survivre au formulaire.
  useEffect(
    () => () => {
      generationRef.current += 1;
      cancelPending();
    },
    [cancelPending]
  );

  return { ...state, setQuery, loadMore, retry, reset };
}
